import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Phone, Mail, Calendar, StickyNote, CheckSquare, FileText, Eye, X, Send, User, MessageSquare, UserPlus, Building2, PhoneIncoming, PhoneOutgoing, FileAudio, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, CALL_RESULT_META } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { useAutoRefresh } from "../lib/autoRefresh";

const TIPOS = [
  { value: "nota", label: "Nota", icon: StickyNote },
  { value: "chamada", label: "Chamada", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
  { value: "reuniao", label: "Reuniao", icon: Calendar },
  { value: "tarefa", label: "Tarefa", icon: CheckSquare },
  { value: "demo", label: "Demo", icon: FileText },
  { value: "chat-ai", label: "Chat AI", icon: MessageSquare },
];

const DEPARTAMENTOS = ["Geral", "CEO", "Financeiro", "DPO", "CISO", "TI", "Compras", "Legal", "Marketing", "Outro"];

export default function Activities() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filterTipo, setFilterTipo] = useState("");
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null); // Ver detalhe
  const [nova, setNova] = useState({ tipo: "nota", account_id: "", contact_id: "", contact_ids: [], assunto: "", corpo: "", prazo: "" });
  // Contactos da conta seleccionada
  const [accountContacts, setAccountContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  // Sub-modal para criar contacto
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ nome: "", cargo: "", departamento: "", email: "", telefone: "", telemovel: "", notas: "", principal: false });

  const load = async () => {
    try {
      const [a, acc] = await Promise.all([
        api.get("/crm/activities"),
        api.get("/crm/accounts/lookup?limit=200"),
      ]);
      setRows(a.data || []);
      setAccounts(acc.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  // Auto-abrir modal com contexto (contact_id, account_id) via query params.
  // Ex.: /app/crm/atividades?contact_id=abc&nova=1 (vindo do ContactDrawer)
  //      /app/crm/atividades?account_id=xyz&nova=1 (vindo da ficha de conta)
  useEffect(() => {
    const shouldOpen = params.get("nova") === "1";
    if (!shouldOpen) return;
    let cancel = false;
    const load2 = async () => {
      const cid = params.get("contact_id") || "";
      const aid = params.get("account_id") || "";
      let accountId = aid;
      // Se veio pelo contacto, descobre o account_id do contacto
      if (cid && !accountId) {
        try {
          const { data } = await api.get(`/crm/contacts/${cid}`);
          if (!cancel && data?.account_id) accountId = data.account_id;
        } catch (e) { /* noop */ }
      }
      if (cancel) return;
      setNova((n) => ({ ...n, account_id: accountId, contact_id: cid,
        contact_ids: cid ? [cid] : [],
        assunto: params.get("assunto") || n.assunto, corpo: params.get("corpo") || n.corpo }));
      setShow(true);
      // Faz scroll para o topo para o modal aparecer visivel
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
      // Limpa query params usados para nao reabrir em re-navegacoes
      const next = new URLSearchParams(params);
      ["nova", "contact_id", "account_id", "assunto", "corpo"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    };
    load2();
    return () => { cancel = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sempre que abre o modal, faz scroll top para garantir visibilidade
  useEffect(() => {
    if (show) { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ } }
  }, [show]);

  // Carrega contactos da conta seleccionada para o picker
  useEffect(() => {
    if (!nova.account_id) { setAccountContacts([]); return; }
    let cancel = false;
    setLoadingContacts(true);
    api.get(`/crm/accounts/${nova.account_id}`).then((r) => {
      if (cancel) return;
      setAccountContacts((r.data && r.data.contactos) || []);
    }).catch(() => { if (!cancel) setAccountContacts([]); })
      .finally(() => { if (!cancel) setLoadingContacts(false); });
    return () => { cancel = true; };
  }, [nova.account_id]);

  const resetNova = () => setNova({ tipo: "nota", account_id: "", contact_id: "", contact_ids: [], assunto: "", corpo: "", prazo: "" });

  const filtered = useMemo(() => (
    filterTipo ? rows.filter((r) => r.tipo === filterTipo) : rows
  ), [rows, filterTipo]);

  const create = async () => {
    if (!nova.assunto.trim()) return toast.error("Indique o assunto.");
    // Multi-contactos: contact_ids = seleccao actual (usa contact_id como fallback compat)
    const cids = (nova.contact_ids && nova.contact_ids.length ? nova.contact_ids : (nova.contact_id ? [nova.contact_id] : []));
    const payload = { ...nova, contact_id: cids[0] || "", contact_ids: cids };
    // Se for email ou tarefa ou reuniao, preparar a acao concreta em vez de so uma nota
    if (nova.tipo === "email") {
      if (!nova.account_id) return toast.error("Escolha a conta para preparar o email.");
      // Regista a atividade e redireciona para a ficha da conta com o compose aberto (assunto/corpo pre-preenchidos)
      try { await api.post("/crm/activities", payload); } catch (e) { /* noop */ }
      toast.message("A abrir compose de email...");
      const cSuffix = nova.contact_id ? `&contact_id=${nova.contact_id}` : "";
      nav(`/app/crm/contas/${nova.account_id}?compose=1&assunto=${encodeURIComponent(nova.assunto)}&corpo=${encodeURIComponent(nova.corpo || "")}${cSuffix}#emails`);
      setShow(false); resetNova(); return;
    }
    if (nova.tipo === "reuniao" || nova.tipo === "demo") {
      if (!nova.account_id) return toast.error("Escolha a conta para agendar.");
      try { await api.post("/crm/activities", payload); } catch (e) { /* noop */ }
      toast.message("A abrir agendamento...");
      nav(`/app/agendamentos?account_id=${nova.account_id}&assunto=${encodeURIComponent(nova.assunto)}&descricao=${encodeURIComponent(nova.corpo || "")}`);
      setShow(false); resetNova(); return;
    }
    if (nova.tipo === "tarefa") {
      try {
        // Cria tarefa real em /api/team/tasks e uma atividade "tarefa criada" na timeline da conta
        await api.post("/team/tasks", {
          titulo: nova.assunto, descricao: nova.corpo || "",
          tipo: "tarefa", prazo: nova.prazo || "",
          ref_tipo: nova.account_id ? "account" : "", ref_id: nova.account_id || "",
        });
        await api.post("/crm/activities", { ...payload, corpo: (nova.corpo || "") + `\n[Tarefa criada com prazo: ${nova.prazo || "sem prazo"}]` });
        toast.success("Tarefa criada e associada.");
        setShow(false); resetNova(); load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      return;
    }
    // Chamada / nota: regista atividade simples
    try {
      await api.post("/crm/activities", payload);
      toast.success("Atividade registada.");
      setShow(false); resetNova(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addNewContact = async () => {
    if (!nova.account_id) return toast.error("Escolha a conta primeiro.");
    if (!newContact.nome.trim()) return toast.error("Indique o nome do contacto.");
    try {
      const telefones = [newContact.telefone, newContact.telemovel].filter((x) => (x || "").trim());
      const { data } = await api.post("/crm/contacts", {
        account_id: nova.account_id,
        nome: newContact.nome, cargo: newContact.cargo, departamento: newContact.departamento,
        email: newContact.email, emails: newContact.email ? [newContact.email] : [],
        telefone: newContact.telefone, telemovel: newContact.telemovel, telefones,
        principal: !!newContact.principal, notas: newContact.notas,
      });
      toast.success("Contacto adicionado.");
      // Injecta na lista local + selecciona-o
      const created = data || { id: "", nome: newContact.nome };
      setAccountContacts((prev) => [...prev, { id: created.id, nome: created.nome || newContact.nome,
                                                  departamento: newContact.departamento,
                                                  email: newContact.email, telemovel: newContact.telemovel,
                                                  principal: !!newContact.principal }]);
      setNova((n) => ({ ...n,
        contact_id: created.id || n.contact_id,
        contact_ids: created.id ? Array.from(new Set([...(n.contact_ids || []), created.id])) : (n.contact_ids || []),
      }));
      setShowNewContact(false);
      setNewContact({ nome: "", cargo: "", departamento: "", email: "", telefone: "", telemovel: "", notas: "", principal: false });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const accName = (id) => accounts.find((a) => a.id === id)?.nome || "-";
  const iconOf = (t) => (TIPOS.find((x) => x.value === t) || TIPOS[0]).icon;

  const cols = [
    { key: "tipo", label: "Tipo", render: (r) => {
      const I = iconOf(r.tipo);
      return <span className="inline-flex items-center gap-1.5"><I size={14} className="text-blue" /><Badge value={r.tipo} /></span>;
    }},
    { key: "assunto", label: "Assunto", render: (r) => (
      r.tipo === "chat-ai" && r.chat_session_id ? (
        <button onClick={() => nav(`/app/inbox-canais?session=${encodeURIComponent(r.chat_session_id)}&from=activity`)} data-testid={`activity-chat-${r.id}`}
          className="font-semibold text-navy hover:text-blue hover:underline text-left inline-flex items-center gap-1.5">
          <MessageSquare size={12} className="text-blue" /> {r.assunto || "(sem assunto)"}
        </button>
      ) : (
        <button onClick={() => setDetail(r)} data-testid={`activity-open-${r.id}`}
          className="font-semibold text-navy hover:text-blue hover:underline text-left">{r.assunto || "(sem assunto)"}</button>
      )
    ) },
    { key: "account_id", label: "Conta", render: (r) => (
      r.account_id ? (
        <button onClick={() => nav(`/app/crm/contas/${r.account_id}`)}
          className="text-navy hover:text-blue hover:underline text-left text-sm" data-testid={`activity-account-${r.id}`}>{accName(r.account_id)}</button>
      ) : <span className="text-muted2">-</span>
    ) },
    { key: "corpo", label: "Descricao", render: (r) => <span className="text-xs text-muted2 line-clamp-2">{r.corpo || "-"}</span> },
    { key: "autor_email", label: "Autor", render: (r) => <span className="text-xs">{r.autor_email || "-"}</span> },
    { key: "em", label: "Data", render: (r) => <span className="text-xs text-muted2">{r.em ? new Date(r.em).toLocaleString("pt-PT") : "-"}</span> },
    { key: "acoes", label: "", render: (r) => (
      <button onClick={() => setDetail(r)} data-testid={`activity-view-${r.id}`} className="text-slate-600 hover:text-blue" title="Ver detalhe">
        <Eye size={15} />
      </button>
    ) },
  ];

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Atividades"
        subtitle="Registo unificado de notas, chamadas, emails, reunioes e tarefas. Ao criar uma acao 'email', 'reuniao' ou 'tarefa' e preparada a acao concreta ligada a conta."
        action={<Btn onClick={() => setShow(true)} testid="new-activity-btn"><Plus size={16} /> Nova atividade</Btn>}
      />

      <div className="mb-4 flex gap-2 items-center" data-testid="activities-filters">
        <button
          onClick={() => setFilterTipo("")}
          data-testid="activities-filter-all"
          className={`px-3 py-1.5 text-xs rounded-md border ${!filterTipo ? "bg-navy text-white border-navy" : "border-tint text-muted2 hover:bg-slate-50"}`}
        >Todos</button>
        {TIPOS.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilterTipo(t.value)}
            data-testid={`activities-filter-${t.value}`}
            className={`px-3 py-1.5 text-xs rounded-md border inline-flex items-center gap-1.5 ${filterTipo === t.value ? "bg-navy text-white border-navy" : "border-tint text-muted2 hover:bg-slate-50"}`}
          ><t.icon size={12} />{t.label}</button>
        ))}
      </div>

      <Table testid="activities-table" columns={cols} rows={filtered} />

      <Modal open={show} onClose={() => setShow(false)} title="Nova atividade" testid="activity-modal">
        <Selecter label="Tipo" value={nova.tipo} onChange={(v) => setNova({ ...nova, tipo: v })} options={TIPOS} testid="activity-tipo" />
        {(nova.tipo === "email" || nova.tipo === "tarefa" || nova.tipo === "reuniao" || nova.tipo === "demo") && (
          <div className="mb-2 rounded-md p-2.5 bg-blue/5 border border-blue/20 text-[12px] text-slate-700" data-testid="activity-smart-hint">
            {nova.tipo === "email" && <>Ao guardar, sera aberto o compose de email na ficha da conta com estes dados pre-preenchidos.</>}
            {nova.tipo === "tarefa" && <>Ao guardar, sera criada uma tarefa em /app/tarefas associada a esta conta, com prazo opcional.</>}
            {(nova.tipo === "reuniao" || nova.tipo === "demo") && <>Ao guardar, sera aberto o agendamento Teams (M365) ja com a conta e o assunto pre-preenchidos.</>}
          </div>
        )}
        {/* Conta - se ja veio pre-seleccionada, mostra chip; senao, permite escolher */}
        {nova.account_id && accounts.find((a) => a.id === nova.account_id) ? (
          <div className="mb-3" data-testid="activity-account-chip">
            <label className="block text-xs uppercase text-muted2 mb-1">Conta</label>
            <div className="flex items-center gap-2 bg-blue/5 border border-blue/20 rounded-md px-3 py-2">
              <Building2 size={14} className="text-blue" />
              <span className="text-navy font-semibold text-sm flex-1">{accName(nova.account_id)}</span>
              <button type="button" onClick={() => setNova({ ...nova, account_id: "", contact_id: "" })}
                data-testid="activity-account-change" className="text-[11px] text-muted2 hover:text-navy underline">alterar</button>
            </div>
          </div>
        ) : (
          <Selecter label={nova.tipo === "nota" ? "Conta (opcional)" : "Conta"} value={nova.account_id}
            onChange={(v) => setNova({ ...nova, account_id: v, contact_id: "" })}
            options={[{ value: "", label: "- Sem conta -" }, ...accounts.map((a) => ({ value: a.id, label: a.nome }))]}
            testid="activity-account" />
        )}
        {/* Contactos - multi-seleccao */}
        {nova.account_id && (
          <div className="mb-3" data-testid="activity-contact-picker">
            <label className="block text-xs uppercase text-muted2 mb-1 flex items-center justify-between">
              <span>Contactos {(nova.contact_ids || []).length > 0 && <span className="text-blue">({nova.contact_ids.length} seleccionado{nova.contact_ids.length > 1 ? "s" : ""})</span>}</span>
              <button type="button" onClick={() => setShowNewContact(true)}
                data-testid="activity-new-contact-btn"
                className="text-[11px] font-semibold text-blue hover:underline inline-flex items-center gap-1">
                <UserPlus size={12} /> Novo contacto
              </button>
            </label>
            {loadingContacts && <div className="text-[12px] text-muted2 italic p-2">A carregar contactos...</div>}
            {!loadingContacts && accountContacts.length === 0 && (
              <p className="text-[12px] text-muted2 italic p-2 bg-slate-50 rounded border border-tint">
                Sem contactos nesta conta. Use <b>Novo contacto</b> para adicionar.
              </p>
            )}
            {!loadingContacts && accountContacts.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-tint rounded-md divide-y divide-slate-100">
                {accountContacts.map((c) => {
                  const checked = (nova.contact_ids || []).includes(c.id);
                  return (
                    <label key={c.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={checked}
                        data-testid={`activity-contact-cb-${c.id}`}
                        onChange={(e) => {
                          const next = new Set(nova.contact_ids || []);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          const arr = Array.from(next);
                          setNova({ ...nova, contact_ids: arr, contact_id: arr[0] || "" });
                        }}
                        className="rounded border-slate-300" />
                      <span className="text-navy font-medium">{c.nome}</span>
                      {c.departamento && <span className="text-[10px] text-slate-500 uppercase">{c.departamento}</span>}
                      {c.principal && <span className="text-[10px] text-emerald-600">*</span>}
                      {c.email && <span className="text-[11px] text-slate-500 ml-auto truncate">{c.email}</span>}
                    </label>
                  );
                })}
              </div>
            )}
            {(nova.contact_ids || []).length > 1 && (
              <p className="text-[11px] text-blue mt-1">Multi-contacto: a atividade sera associada a {nova.contact_ids.length} contactos.</p>
            )}
          </div>
        )}
        <Field label={nova.tipo === "email" ? "Assunto do email" : (nova.tipo === "tarefa" ? "Titulo da tarefa" : "Assunto")}
          value={nova.assunto} onChange={(v) => setNova({ ...nova, assunto: v })} testid="activity-assunto" />
        <div className="mb-3">
          <label className="block text-xs uppercase text-muted2 mb-1">
            {nova.tipo === "email" ? "Corpo do email (esboco)" : (nova.tipo === "tarefa" ? "Descricao da tarefa" : "Descricao")}
          </label>
          <textarea
            value={nova.corpo}
            onChange={(e) => setNova({ ...nova, corpo: e.target.value })}
            data-testid="activity-corpo"
            rows={4}
            className="w-full px-3 py-2 text-sm border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-blue/20"
          />
        </div>
        {nova.tipo === "tarefa" && (
          <Field label="Prazo (opcional)" type="date" value={nova.prazo} onChange={(v) => setNova({ ...nova, prazo: v })} testid="activity-prazo" />
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn>
          <Btn onClick={create} testid="activity-save">
            {nova.tipo === "email" && <><Send size={13} /> Preparar email</>}
            {nova.tipo === "tarefa" && <><CheckSquare size={13} /> Criar tarefa</>}
            {(nova.tipo === "reuniao" || nova.tipo === "demo") && <><Calendar size={13} /> Preparar reuniao</>}
            {(nova.tipo === "nota" || nova.tipo === "chamada") && <>Registar</>}
          </Btn>
        </div>
      </Modal>

      {/* Sub-modal: Novo contacto (mesmas regras que na ficha da conta) */}
      <Modal open={showNewContact} onClose={() => setShowNewContact(false)} title="Adicionar contacto" testid="activity-new-contact-modal" size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome" value={newContact.nome} onChange={(v) => setNewContact({ ...newContact, nome: v })} testid="new-contact-nome" />
          <div>
            <label className="block text-xs uppercase text-muted2 mb-1">Departamento</label>
            <select value={newContact.departamento}
              onChange={(e) => setNewContact({ ...newContact, departamento: e.target.value })}
              data-testid="new-contact-departamento"
              className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm">
              <option value="">- Sem departamento -</option>
              {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <Field label="Cargo (opcional)" value={newContact.cargo} onChange={(v) => setNewContact({ ...newContact, cargo: v })} testid="new-contact-cargo" />
          <Field label="Email" value={newContact.email} onChange={(v) => setNewContact({ ...newContact, email: v })} testid="new-contact-email" />
          <Field label="Telefone" value={newContact.telefone} onChange={(v) => setNewContact({ ...newContact, telefone: v })} testid="new-contact-telefone" />
          <Field label="Telemovel" value={newContact.telemovel} onChange={(v) => setNewContact({ ...newContact, telemovel: v })} testid="new-contact-telemovel" />
        </div>
        <label className="flex items-center gap-2 text-sm text-navy cursor-pointer mt-2">
          <input type="checkbox" checked={!!newContact.principal}
            onChange={(e) => setNewContact({ ...newContact, principal: e.target.checked })}
            data-testid="new-contact-principal" className="rounded border-slate-300" />
          Contacto principal
        </label>
        <TextArea label="Notas" value={newContact.notas} onChange={(v) => setNewContact({ ...newContact, notas: v })} testid="new-contact-notas" />
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShowNewContact(false)}>Cancelar</Btn>
          <Btn onClick={addNewContact} testid="new-contact-save">Adicionar contacto</Btn>
        </div>
      </Modal>

      {detail && (
        <ActivityDetailModal detail={detail} accName={accName} iconOf={iconOf} onClose={() => setDetail(null)} />
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Modal de detalhe de atividade - versao rica com suporte a chamadas LetsCall
// ---------------------------------------------------------------------------
function ActivityDetailModal({ detail, accName, iconOf, onClose }) {
  const nav = useNavigate();
  const [transcription, setTranscription] = useState(null);
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);

  const isChamada = detail.tipo === "chamada";
  const meta = detail.meta || {};
  const callId = meta.letscall_call_id;
  const durSec = meta.duration_seconds;
  const durStr = durSec != null ? (durSec >= 60 ? `${Math.floor(durSec / 60)}min ${durSec % 60}s` : `${durSec}s`) : null;
  const extNum = meta.direction === "outbound" ? meta.callee_number : meta.caller_number;
  const callResultMeta = CALL_RESULT_META[detail.resultado];

  const fetchTranscription = async () => {
    if (!callId) return;
    setLoadingTranscription(true);
    try {
      const { data } = await api.get(`/channels/letscall/transcription/${callId}`);
      setTranscription(data.transcription);
      setShowTranscription(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcricao nao disponivel.");
    } finally {
      setLoadingTranscription(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      {...overlayCloseHandlers(onClose)} data-testid="activity-detail-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl border border-tint"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-tint sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 text-navy font-semibold">
            {(() => { const I = iconOf(detail.tipo); return <I size={16} className="text-blue" />; })()}
            {detail.assunto || "(sem assunto)"}
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy" data-testid="activity-detail-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4 text-sm">
          {/* Campos base */}
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-muted2 text-xs">Tipo:</span> <Badge value={detail.tipo} /></div>
            <div><span className="text-muted2 text-xs">Data:</span> {detail.em ? new Date(detail.em).toLocaleString("pt-PT") : "-"}</div>
            <div><span className="text-muted2 text-xs">Autor:</span> {detail.autor_email || "LetsCall One"}</div>
            <div>
              <span className="text-muted2 text-xs">Conta:</span>{" "}
              {detail.account_id ? (
                <button onClick={() => nav(`/app/crm/contas/${detail.account_id}`)}
                  className="text-blue hover:underline text-sm inline-flex items-center gap-1" data-testid="activity-detail-account">
                  <User size={12} /> {accName(detail.account_id)}
                </button>
              ) : <span className="text-muted2">-</span>}
            </div>
          </div>

          {/* Painel especifico para chamadas */}
          {isChamada && (
            <div className="border border-blue/20 bg-blue/5 rounded-lg p-3 space-y-2" data-testid="activity-call-details">
              <div className="flex items-center gap-2 text-navy font-semibold text-xs uppercase tracking-wide">
                <Phone size={12} className="text-blue" /> Detalhes da chamada
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                {meta.direction && (
                  <div className="flex items-center gap-1.5">
                    {meta.direction === "outbound" ? <PhoneOutgoing size={12} className="text-slate-500" /> : <PhoneIncoming size={12} className="text-slate-500" />}
                    <span className="text-muted2">Direcao:</span>
                    <span className="font-medium text-navy">{meta.direction === "outbound" ? "Efetuada" : "Recebida"}</span>
                  </div>
                )}
                {durStr && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted2">Duracao:</span>
                    <span className="font-semibold text-navy">{durStr}</span>
                  </div>
                )}
                {extNum && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted2">Numero:</span>
                    <span className="font-mono text-navy">{extNum}</span>
                  </div>
                )}
                {meta.extension && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted2">Extensao:</span>
                    <span className="font-mono text-navy">{meta.extension}</span>
                  </div>
                )}
                {detail.resultado && (
                  <div className="col-span-2 mt-0.5">
                    {callResultMeta ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${callResultMeta.badge}`}>
                        <callResultMeta.icon size={10} /> {callResultMeta.label}
                      </span>
                    ) : (
                      <Badge value={detail.resultado} />
                    )}
                  </div>
                )}
              </div>

              {/* Botao de transcricao */}
              {callId && (
                <div className="pt-2 border-t border-blue/20">
                  {!showTranscription ? (
                    <button
                      onClick={fetchTranscription}
                      disabled={loadingTranscription}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy disabled:opacity-50"
                      data-testid="activity-transcription-btn">
                      <FileAudio size={13} />
                      {loadingTranscription ? "A carregar transcricao..." : "Ver transcricao"}
                    </button>
                  ) : (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => setShowTranscription(!showTranscription)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy"
                        data-testid="activity-transcription-toggle">
                        <FileAudio size={13} />
                        Transcricao
                        {showTranscription ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                      {showTranscription && transcription && (
                        <div className="bg-white border border-blue/20 rounded p-2 text-[11px] text-navy max-h-40 overflow-y-auto whitespace-pre-wrap"
                          data-testid="activity-transcription-text">
                          {typeof transcription === "string"
                            ? transcription
                            : transcription.text || transcription.transcript || JSON.stringify(transcription, null, 2)}
                        </div>
                      )}
                      {showTranscription && !transcription && (
                        <div className="text-xs text-muted2 italic" data-testid="activity-transcription-empty">Transcricao nao disponivel para esta chamada.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Descricao / corpo */}
          <div className="border-t border-tint pt-3">
            <div className="text-muted2 text-xs uppercase tracking-wider mb-1">Descricao</div>
            <div className="whitespace-pre-wrap text-navy text-sm">
              {detail.corpo || <span className="text-muted2">Sem descricao.</span>}
            </div>
          </div>

          {detail.deal_id && (
            <div className="border-t border-tint pt-2 text-xs text-muted2">Oportunidade: {detail.deal_id}</div>
          )}
          {detail.contact_id && (
            <div className="text-xs text-muted2">Contacto: {detail.contact_id}</div>
          )}
        </div>
      </div>
    </div>
  );
}

